import React, { useState, useEffect } from 'react';
import { operatorsAPI } from '../services/api';
import { FiTrendingUp } from 'react-icons/fi';
import { formatDuration } from '../utils/formatters';
import RatingStars from '../components/RatingStars';
import toast from 'react-hot-toast';

const PERIOD_LABELS = {
  today: 'Bugun',
  week: 'Bu hafta',
  month: 'Bu oy',
  year: 'Bu yil',
};

export default function ProductivityPage() {
  const [data, setData] = useState({ operators: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');

  const load = async () => {
    setLoading(true);
    try {
      const res = await operatorsAPI.productivity({ period });
      setData(res.data);
    } catch (err) {
      toast.error('Ma\'lumotlarni olishda xato');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [period]);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  const { operators = [], totals = {} } = data;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: '#f1f5f9', margin: 0 }}>
          Unumdorlik
        </h1>

        <div style={styles.periodTabs}>
          {Object.entries(PERIOD_LABELS).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setPeriod(key)}
              style={{
                ...styles.periodBtn,
                ...(period === key ? styles.periodBtnActive : {}),
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <span className="stat-label">Jami javob berildi</span>
          <span className="stat-value" style={{ color: '#10b981' }}>{totals.total_answered || 0}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Jami gaplashish</span>
          <span className="stat-value">{formatDuration(totals.total_talk_time || 0)}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Javobsiz (bog'lanilmagan)</span>
          <span className="stat-value" style={{ color: '#ef4444' }}>{totals.total_missed || 0}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Qayta bog'lanildi</span>
          <span className="stat-value" style={{ color: '#3b82f6' }}>{totals.total_followed_up || 0}</span>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <span className="card-title">Operator ro'yxati — {PERIOD_LABELS[period]}</span>
        </div>

        {operators.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>
            Operatorlar yo'q
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Operator</th>
                  <th>Holat</th>
                  <th style={{ textAlign: 'center' }}>Javob berildi</th>
                  <th style={{ textAlign: 'center' }}>Kiruvchi</th>
                  <th style={{ textAlign: 'center' }}>Chiquvchi</th>
                  <th style={{ textAlign: 'center' }}>Qayta bog'landi</th>
                  <th>Jami gaplashish</th>
                  <th>O'rtacha</th>
                  <th>Eng uzun</th>
                  <th style={{ textAlign: 'center' }}>Baho (CSAT)</th>
                </tr>
              </thead>
              <tbody>
                {operators.map((op, idx) => (
                  <tr key={op.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: '50%',
                          background: getAvatarColor(idx),
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: 'white', fontSize: 13, fontWeight: 700,
                        }}>
                          {op.full_name?.[0]?.toUpperCase() || 'O'}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600 }}>{op.full_name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                            #{op.sip_extension}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                        background: getStatusBg(op.status),
                        color: getStatusColor(op.status),
                      }}>
                        <span style={{
                          width: 6, height: 6, borderRadius: '50%',
                          background: getStatusColor(op.status),
                        }} />
                        {getStatusLabel(op.status)}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: 700, color: '#10b981', fontSize: 18 }}>
                      {op.answered_count}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ color: '#3b82f6' }}>{op.inbound_answered}</span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ color: '#f59e0b' }}>{op.outbound_answered}</span>
                    </td>
                    <td style={{ textAlign: 'center', color: '#3b82f6', fontWeight: 600 }}>
                      {op.followed_up_count || 0}
                    </td>
                    <td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                      {formatDuration(op.total_talk_time)}
                    </td>
                    <td style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)' }}>
                      {formatDuration(op.avg_duration)}
                    </td>
                    <td style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)' }}>
                      {formatDuration(op.max_duration)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {op.rated_count > 0 ? (
                        <div title={`${op.rated_count} ta baho`} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.3 }}>
                          <RatingStars value={Math.round(op.avg_rating)} />
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{op.avg_rating.toFixed(1)} · {op.rated_count} ta</span>
                        </div>
                      ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {operators.length > 0 && operators[0].answered_count > 0 && (
        <div className="card" style={{ marginTop: 24, padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <FiTrendingUp size={20} color="#10b981" />
            <span className="card-title">TOP 3 operator — {PERIOD_LABELS[period]}</span>
          </div>
          <div className="adm-g3" style={{ gap: 16 }}>
            {operators.slice(0, 3).map((op, idx) => (
              <div key={op.id} style={{
                padding: 16,
                background: idx === 0 ? 'rgba(250, 204, 21, 0.08)' : idx === 1 ? 'rgba(156, 163, 175, 0.08)' : 'rgba(245, 158, 11, 0.08)',
                border: `1px solid ${idx === 0 ? 'rgba(250, 204, 21, 0.3)' : idx === 1 ? 'rgba(156, 163, 175, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                borderRadius: 10,
              }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
                  {idx === 0 ? '🥇 TOP 1' : idx === 1 ? '🥈 TOP 2' : '🥉 TOP 3'}
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, marginTop: 6 }}>{op.full_name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  {op.answered_count} qo'ng'iroq · {formatDuration(op.total_talk_time)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function getStatusLabel(s) {
  return s === 'online' ? 'Onlayn' : s === 'break' ? 'Tanaffus' : 'Oflayn';
}
function getStatusColor(s) {
  return s === 'online' ? '#10b981' : s === 'break' ? '#f59e0b' : '#6b7280';
}
function getStatusBg(s) {
  return s === 'online' ? 'rgba(16,185,129,0.12)' 
       : s === 'break' ? 'rgba(245,158,11,0.12)' 
       : 'rgba(107,114,128,0.12)';
}
function getAvatarColor(idx) {
  const colors = [
    'linear-gradient(135deg, #3b82f6, #6366f1)',
    'linear-gradient(135deg, #10b981, #059669)',
    'linear-gradient(135deg, #f59e0b, #d97706)',
    'linear-gradient(135deg, #8b5cf6, #7c3aed)',
    'linear-gradient(135deg, #ef4444, #dc2626)',
  ];
  return colors[idx % colors.length];
}

const styles = {
  periodTabs: {
    display: 'flex', gap: 4, background: '#0f172a',
    padding: 4, borderRadius: 10, border: '1px solid #2d3a4f',
  },
  periodBtn: {
    padding: '8px 16px', background: 'transparent',
    border: 'none', borderRadius: 8,
    color: 'var(--text-muted)', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  periodBtnActive: {
    background: '#3b82f6', color: 'white',
  },
};
